import { createApi } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import { baseQuery } from '../baseQuery';

export interface ItemSearchItem {
  itemid: number;
  itemcode: string | null;
  itemname: string | null;
  itemtype: string | null;
  is_active: number | null;
  salesinvoiceflag: number | null;
  purchaseinvoiceflag: number | null;
}

interface ItemSearchApiResponse {
  itemid?: unknown;
  itemcode?: unknown;
  itemname?: unknown;
  itemtype?: unknown;
  is_active?: unknown;
  salesinvoiceflag?: unknown;
  purchaseinvoiceflag?: unknown;
}

export interface ItemSearchScope {
  companyId: number;
}

interface ItemSearchVariables {
  word: string;
  type: string;
  rowfrom: number;
  limitno: number;
}

interface ItemSearchRequest {
  body: string;
  variables: ItemSearchVariables;
}

interface ItemSearchError {
  message: string;
  status: number;
}

const PAGE_SIZE = 500;
// The selector only needs identity, type and eligibility; pricing is fetched on selection.
const ITEM_SEARCH_QUERY = `
  query MergedItemMasterSearch($word: String!, $type: String!, $rowfrom: Int!, $limitno: Int!) {
    listItemMasterSearch(word: $word, type: $type, rowfrom: $rowfrom, limitno: $limitno) {
      itemid
      itemcode
      itemname
      itemtype
      is_active
      salesinvoiceflag
      purchaseinvoiceflag
    }
  }
`;

const itemBaseQuery: BaseQueryFn<ItemSearchRequest, unknown, ItemSearchError> = baseQuery;

function isItemSearchItem(value: unknown): value is ItemSearchItem {
  if (value === null || typeof value !== 'object') return false;
  const item: ItemSearchApiResponse = value;
  return (
    typeof item.itemid === 'number' &&
    (item.itemcode === null || typeof item.itemcode === 'string') &&
    (item.itemname === null || typeof item.itemname === 'string') &&
    (item.itemtype === null || typeof item.itemtype === 'string') &&
    (item.is_active === null || typeof item.is_active === 'number') &&
    (item.salesinvoiceflag === null || typeof item.salesinvoiceflag === 'number') &&
    (item.purchaseinvoiceflag === null || typeof item.purchaseinvoiceflag === 'number')
  );
}

export const itemMasterApi = createApi({
  reducerPath: 'itemMasterApi',
  baseQuery: itemBaseQuery,
  keepUnusedDataFor: 300,
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    // Company is part of the cache key; the API resolves it from the authenticated session.
    listMergedItems: builder.query<ItemSearchItem[], ItemSearchScope>({
      async queryFn(_scope, api, _extraOptions, fetchPage) {
        const items: ItemSearchItem[] = [];
        let rowfrom = 0;
        while (!api.signal.aborted) {
          const result = await fetchPage({
            body: ITEM_SEARCH_QUERY,
            variables: { word: '', type: '', rowfrom, limitno: PAGE_SIZE },
          });
          if (result.error) return { error: result.error };
          if (!Array.isArray(result.data)) {
            return { error: { status: 500, message: 'Invalid item search response' } };
          }
          const page: unknown[] = result.data;
          if (!page.every((item) => item === null || isItemSearchItem(item))) {
            return { error: { status: 500, message: 'Invalid item search row' } };
          }
          items.push(...page.filter(isItemSearchItem));
          if (page.length < PAGE_SIZE) return { data: items };
          rowfrom += PAGE_SIZE;
        }
        return { error: { status: 499, message: 'Item search cancelled' } };
      },
    }),
  }),
});

export const { useListMergedItemsQuery } = itemMasterApi;
